// components/admin/NovaClinicaForm.jsx
import React, { useState, useContext } from "react";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { useLoading } from "../../shared/context/LoadingContext";
import { AuthContext } from "../../shared/context/auth-context";

// import "./NovaClinica.scss";
const NovaClinicaForm = () => {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();
  const [currentStep, setCurrentStep] = useState(1);
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);

  // Estado para os dados da clínica
  const [formData, setFormData] = useState({
    // Dados da clínica
    nome: "",
    nomeFiscal: "",
    cidade: "",
    endereco: "",
    ilha: "",
    contacto: "",
    codigoPostal: "",
    email: "",
    nif: "",
    nrCertidao: "",
    logotipo: "",

    // Dados do emissor (opcional)
    dadosEmissor: {
      cliente_id: "",
      cliente_oauth_secret: "",
      led: "",
      serie: "",
    },

    // Dados do administrador
    adminNome: "",
    adminEmail: "",
    adminContacto: "",
    adminGenero: "M",
    adminPassword: "",
    adminConfirmPassword: "",
  });

  // Função para manipular mudanças nos inputs
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Função para validar os dados por etapa
  const validateStep = (step) => {
    if (step === 1) {
      const {
        nome,
        nomeFiscal,
        cidade,
        endereco,
        ilha,
        contacto,
        email,
        nif,
        nrCertidao,
      } = formData;
      if (
        !nome ||
        !nomeFiscal ||
        !cidade ||
        !endereco ||
        !ilha ||
        !contacto ||
        !email ||
        !nif ||
        !nrCertidao
      ) {
        toast.error(
          "Por favor, preencha todos os campos obrigatórios da clínica"
        );
        return false;
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error("Por favor, insira um email válido para a clínica");
        return false;
      }

      return true;
    }

    if (step === 2) {
      // Step 2 não tem campos obrigatórios (dados do emissor)
      return true;
    }

    if (step === 3) {
      const {
        adminNome,
        adminEmail,
        adminContacto,
        adminPassword,
        adminConfirmPassword,
      } = formData;
      if (
        !adminNome ||
        !adminEmail ||
        !adminContacto ||
        !adminPassword ||
        !adminConfirmPassword
      ) {
        toast.error("Por favor, preencha todos os campos do administrador");
        return false;
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(adminEmail)) {
        toast.error("Por favor, insira um email válido para o administrador");
        return false;
      }

      // Validar senha
      if (adminPassword.length < 6) {
        toast.error("A senha deve ter pelo menos 6 caracteres");
        return false;
      }

      // Validar confirmação de senha
      if (adminPassword !== adminConfirmPassword) {
        toast.error("As senhas não coincidem");
        return false;
      }

      return true;
    }

    return false;
  };

  // Função para avançar para a próxima etapa
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Função para voltar para a etapa anterior
  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Função para submeter o formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      return;
    }

    startLoading();

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_LINK}/api/clinicas/onboarding`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + auth.token,
          },
          body: JSON.stringify(formData),
        }
      );

      toast.success("Clínica criada com sucesso!");
    } catch (error) {
      console.error("Erro ao criar clínica:", error);

      const errorMessage =
        error.response?.data?.message ||
        "Ocorreu um erro ao criar a clínica. Por favor, tente novamente.";

      toast.error(errorMessage);
    } finally {
      stopLoading();
    }
  };

  // Renderização do formulário baseado na etapa atual
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-container">
            <h3 className="step-title">Informações Básicas da Clínica</h3>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Nome da Clínica *</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Nome Fiscal *</label>
                <input
                  type="text"
                  name="nomeFiscal"
                  value={formData.nomeFiscal}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Ilha *</label>
                <select
                  name="ilha"
                  value={formData.ilha}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  <option value="">Selecione uma ilha</option>
                  <option value="Santiago">Santiago</option>
                  <option value="São Vicente">São Vicente</option>
                  <option value="Santo Antão">Santo Antão</option>
                  <option value="Sal">Sal</option>
                  <option value="Boavista">Boavista</option>
                  <option value="São Nicolau">São Nicolau</option>
                  <option value="Maio">Maio</option>
                  <option value="Fogo">Fogo</option>
                  <option value="Brava">Brava</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Cidade *</label>
                <input
                  type="text"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Endereço *</label>
                <input
                  type="text"
                  name="endereco"
                  value={formData.endereco}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Código Postal</label>
                <input
                  type="text"
                  name="codigoPostal"
                  value={formData.codigoPostal}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Contacto *</label>
                <input
                  type="text"
                  name="contacto"
                  value={formData.contacto}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">NIF *</label>
                <input
                  type="text"
                  name="nif"
                  value={formData.nif}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Nº Certidão *</label>
                <input
                  type="text"
                  name="nrCertidao"
                  value={formData.nrCertidao}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">URL do Logotipo</label>
                <input
                  type="text"
                  name="logotipo"
                  value={formData.logotipo}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-container">
            <h3 className="step-title">Dados do Emissor (Opcional)</h3>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Cliente ID</label>
                <input
                  type="text"
                  name="dadosEmissor.cliente_id"
                  value={formData.dadosEmissor.cliente_id}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Cliente OAuth Secret</label>
                <input
                  type="text"
                  name="dadosEmissor.cliente_oauth_secret"
                  value={formData.dadosEmissor.cliente_oauth_secret}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">LED</label>
                <input
                  type="text"
                  name="dadosEmissor.led"
                  value={formData.dadosEmissor.led}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Série</label>
                <input
                  type="text"
                  name="dadosEmissor.serie"
                  value={formData.dadosEmissor.serie}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-container">
            <h3 className="step-title">Dados do Administrador</h3>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Nome Completo *</label>
                <input
                  type="text"
                  name="adminNome"
                  value={formData.adminNome}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="adminEmail"
                  value={formData.adminEmail}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Contacto *</label>
                <input
                  type="text"
                  name="adminContacto"
                  value={formData.adminContacto}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Gênero *</label>
                <select
                  name="adminGenero"
                  value={formData.adminGenero}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Senha *</label>
                <input
                  type="password"
                  name="adminPassword"
                  value={formData.adminPassword}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
                <p className="form-hint">Mínimo de 6 caracteres</p>
              </div>

              <div className="form-group">
                <label className="form-label">Confirmar Senha *</label>
                <input
                  type="password"
                  name="adminConfirmPassword"
                  value={formData.adminConfirmPassword}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Componente de formulário para adicionar usuário
  const AdicionarUsuarioForm = () => {
    const [userData, setUserData] = useState({
      name: "",
      genero: "M",
      email: "",
      contacto: "",
      profissao: "",
      password: "",
      confirmPassword: "",
      ativo: true,
      clinica: "", // ID da clínica (ObjectId)
      role: "", // ID do papel (ObjectId)
      listaRoles: [],
      perfis: [], // Array de IDs de perfis (ObjectId[])
    });

    const { sendRequest } = useHttpClient();
    const { startLoading, stopLoading } = useLoading();
    const auth = useContext(AuthContext);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setUserData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePerfisChange = (e) => {
      // Converte os valores selecionados no select multiple para array
      const options = e.target.options;
      const selectedValues = [];

      for (let i = 0; i < options.length; i++) {
        if (options[i].selected) {
          selectedValues.push(options[i].value);
        }
      }

      setUserData((prev) => ({ ...prev, perfis: selectedValues }));
    };

    const handleSubmit = async (e) => {
      startLoading();

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_LINK}/api/users/signup`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + auth.token,
            },
            body: null,
          }
        );

        toast.success("Usuário criado com sucesso!");
      } catch (error) {
        console.error("Erro ao criar usuário:", error);
        toast.error(error.message || "Ocorreu um erro ao criar o usuário");
      } finally {
        stopLoading();
      }
    };

    return (
      <div className="adicionar-usuario-container">
        <h3 className="section-title">Adicionar Novo Usuário</h3>
        <div className="form-actions">
          <button className="btn btn-success" onClick={handleSubmit}>
            Adicionar Usuário
          </button>
        </div>
      </div>
    );
  };

  return (
    // AdicionarUsuarioForm();
    <div className="nova-clinica-container">
      <h2 className="page-title">Onboarding de Nova Clínica</h2>

      {/* Indicador de progresso */}
      <div className="progress-container">
        <div className="progress-wrapper">
          <div className={`step-indicator ${currentStep >= 1 ? "active" : ""}`}>
            <div className="step-number">1</div>
            <span className="step-text">Informações Básicas</span>
          </div>

          <div className="step-line"></div>

          <div className={`step-indicator ${currentStep >= 2 ? "active" : ""}`}>
            <div className="step-number">2</div>
            <span className="step-text">Dados do Emissor</span>
          </div>

          <div className="step-line"></div>

          <div className={`step-indicator ${currentStep >= 3 ? "active" : ""}`}>
            <div className="step-number">3</div>
            <span className="step-text">Administrador</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {renderStep()}

        <div className="form-actions">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="btn btn-secondary"
            >
              Voltar
            </button>
          )}

          {currentStep < 3 ? (
            <button
              type="button"
              onClick={nextStep}
              className="btn btn-primary"
            >
              Próximo
            </button>
          ) : (
            <button type="submit" className="btn btn-success">
              Criar Clínica
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default NovaClinicaForm;
